import { useState } from "react";
import { CalendarClock, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// Optional due date on a note. Author + managers/admins (note.can_assign) can set/clear.
export function DueDateControl({ note, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const due = note.due_date || "";
  const overdue = due && !note.resolved && due < todayStr();
  const canSet = note.can_assign;

  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/notes/${note.note_id}/due`, { due_date: value || null });
      onUpdated?.(data);
      setEditing(false);
      toast.success(value ? "Due date set" : "Due date cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set due date");
    } finally { setBusy(false); }
  };

  const chipCls = overdue
    ? "bg-red-500/15 text-red-300 border-red-500/40"
    : due ? "bg-zinc-800 text-zinc-300 border-zinc-700" : "border-zinc-700 text-zinc-400";

  if (!canSet) {
    if (!due) return null;
    return (
      <span data-testid={`note-due-${note.note_id}`}
        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${chipCls}`}>
        <CalendarClock className="h-2.5 w-2.5" /> Due {fmt(due)}{overdue ? " · overdue" : ""}
      </span>
    );
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input type="date" autoFocus defaultValue={due} disabled={busy}
          data-testid={`note-due-input-${note.note_id}`}
          onChange={(e) => save(e.target.value)}
          onBlur={() => setEditing(false)}
          className="text-[11px] bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/60" />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-0.5">
      <button type="button" onClick={() => setEditing(true)} disabled={busy}
        data-testid={`note-due-${note.note_id}`}
        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors hover:border-indigo-500/40 ${chipCls}`}>
        <CalendarClock className="h-2.5 w-2.5" />
        {due ? <>Due {fmt(due)}{overdue ? " · overdue" : ""}</> : "Set due"}
      </button>
      {due && (
        <button type="button" onClick={() => save("")} disabled={busy}
          data-testid={`note-due-clear-${note.note_id}`} title="Clear due date"
          className="text-zinc-500 hover:text-red-400"><X className="h-3 w-3" /></button>
      )}
    </span>
  );
}
